import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { StrokesToParBadge } from '../components/StrokesToParBadge';
import { Input } from '../components/ui/input';
import {
  Heart, MessageCircle, Send, Compass, Image as ImageIcon, X,
  Plus, MoreHorizontal, MapPin, Trophy, Users, RefreshCw,
  ThumbsUp, ThumbsDown, Sparkles, Clock, ArrowLeft, Trash2,
  Flag, ChevronDown, ChevronUp, Target, Crosshair, Dumbbell, Play,
  UserPlus, UserCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import Navigation from '../components/Navigation';
import TooltipGuide from '../components/TooltipGuide';
import { TIER_STYLES, TierBadge, TierPill, getUserTier } from '../components/TierBadge';
import { ReportBlockMenu } from '../components/moderation/ReportBlockMenu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getPhotoUrl = (photoPath) => {
  if (!photoPath) return null;
  if (photoPath.startsWith('http')) return photoPath;
  if (photoPath.startsWith('/uploads/')) {
    const filename = photoPath.replace('/uploads/', '');
    return `${BACKEND_URL}/api/photos/${filename}`;
  }
  return `${BACKEND_URL}${photoPath}`;
};

const getDefaultPhoto = (name) => {
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'G')}&size=200&background=076652&color=fff&font-size=0.4`;
};

const formatTime = (dateString) => {
  try {
    return formatDistanceToNow(new Date(dateString), { addSuffix: true });
  } catch {
    return '';
  }
};

// ============== Grint-Style Round Card for Feed ==============
const RoundPostCard = ({ roundData }) => {
  const [showScorecard, setShowScorecard] = useState(false);
  const [expandedHole, setExpandedHole] = useState(null);
  const s = roundData?.summary || {};
  const toPar = s.score_to_par;
  const toParStr = toPar === 0 ? 'E' : toPar > 0 ? `+${toPar}` : `${toPar}`;
  const toParColor = toPar < 0 ? 'text-red-500' : toPar === 0 ? 'text-foreground' : 'text-blue-500';

  const getScoreBadge = (diff) => {
    if (diff <= -2) return 'rounded-full border-2 border-yellow-500 text-yellow-500 ring-1 ring-yellow-500/30';
    if (diff === -1) return 'rounded-full border-2 border-red-500 text-red-500';
    if (diff === 0) return 'text-foreground';
    if (diff === 1) return 'rounded-sm border-2 border-blue-500 text-blue-500';
    return 'rounded-sm border-2 border-blue-600 text-blue-600 ring-1 ring-blue-600/30';
  };

  if (!s.total_strokes) return null;

  return (
    <div className="mb-3">
      {/* Stats Card */}
      <div className="rounded-xl bg-gradient-to-br from-primary/8 to-primary/3 border border-primary/20 overflow-hidden">
        <div className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Flag className="w-3.5 h-3.5 text-primary" />
            <span className="text-[10px] font-bold text-primary uppercase tracking-widest">Round Complete</span>
          </div>

          {/* Score Hero */}
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs text-muted-foreground mb-0.5">{roundData.course_name || ''}</div>
              <div className="text-xs text-muted-foreground/70">{roundData.tee_name || ''} · {s.holes_played || roundData.num_holes || 18} holes</div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-black text-foreground leading-none">{s.total_strokes}</div>
              <div className={`text-sm font-bold ${toParColor}`}>{toParStr}</div>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-4 gap-2 mt-4">
            <div className="text-center p-2 rounded-lg bg-background/60">
              <div className="text-lg font-bold text-foreground">{s.total_putts || '-'}</div>
              <div className="text-[9px] text-muted-foreground font-semibold uppercase">Putts</div>
            </div>
            <div className="text-center p-2 rounded-lg bg-background/60">
              <div className="text-lg font-bold text-foreground">{s.gir_pct != null ? `${s.gir_pct}%` : '-'}</div>
              <div className="text-[9px] text-muted-foreground font-semibold uppercase">GIR</div>
            </div>
            <div className="text-center p-2 rounded-lg bg-background/60">
              <div className="text-lg font-bold text-foreground">{s.fairway_pct != null ? `${s.fairway_pct}%` : '-'}</div>
              <div className="text-[9px] text-muted-foreground font-semibold uppercase">Fairways</div>
            </div>
            <div className="text-center p-2 rounded-lg bg-background/60">
              <div className="text-lg font-bold text-foreground">{s.holes_played || roundData.num_holes || '-'}</div>
              <div className="text-[9px] text-muted-foreground font-semibold uppercase">Holes</div>
            </div>
          </div>
        </div>

        {/* Expandable Scorecard Toggle */}
        {roundData.hole_scores && roundData.hole_scores.length > 0 && (
          <>
            <button
              onClick={() => setShowScorecard(!showScorecard)}
              className="w-full flex items-center justify-center gap-1.5 py-2 border-t border-primary/10 text-xs font-semibold text-primary hover:bg-primary/5 transition-colors"
              data-testid="toggle-scorecard-btn"
            >
              {showScorecard ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              {showScorecard ? 'Hide Scorecard' : 'View Scorecard'}
            </button>

            {showScorecard && (
              <div className="border-t border-primary/10 overflow-x-auto">
                <table className="w-full text-[10px]">
                  <thead>
                    <tr className="bg-muted/40">
                      <th className="py-1.5 px-2 text-left font-bold text-muted-foreground w-10">Hole</th>
                      {roundData.hole_scores.map((h) => (
                        <th key={h.hole_number} className="py-1.5 px-1 text-center font-bold text-muted-foreground min-w-[24px]">{h.hole_number}</th>
                      ))}
                      <th className="py-1.5 px-2 text-center font-bold text-muted-foreground bg-muted/50 w-10">Tot</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-border/20">
                      <td className="py-1 px-2 font-bold text-muted-foreground">Par</td>
                      {roundData.hole_scores.map((h) => (
                        <td key={h.hole_number} className="py-1 px-1 text-center text-muted-foreground">{h.par}</td>
                      ))}
                      <td className="py-1 px-2 text-center font-semibold text-muted-foreground bg-muted/30">{s.total_par}</td>
                    </tr>
                    <tr>
                      <td className="py-1 px-2 font-bold text-foreground">Score</td>
                      {roundData.hole_scores.map((h) => {
                        const diff = h.strokes - h.par;
                        return (
                          <td key={h.hole_number} className="py-1 px-1 text-center">
                            <button
                              onClick={(e) => { e.stopPropagation(); setExpandedHole(expandedHole === h.hole_number ? null : h.hole_number); }}
                              className="cursor-pointer"
                            >
                              <span className={`inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold ${getScoreBadge(diff)}`}>
                                {h.strokes}
                              </span>
                            </button>
                          </td>
                        );
                      })}
                      <td className="py-1 px-2 text-center font-bold text-foreground bg-muted/30">{s.total_strokes}</td>
                    </tr>
                  </tbody>
                </table>
                {/* Expanded hole detail */}
                {expandedHole && (() => {
                  const h = roundData.hole_scores.find(s => s.hole_number === expandedHole);
                  if (!h) return null;
                  const pd = h.penalty_details || {};
                  const bd = h.bunker_details || {};
                  return (
                    <div className="p-3 bg-muted/20 border-t border-primary/10" data-testid={`feed-hole-detail-${expandedHole}`}>
                      <div className="text-[10px] font-bold text-foreground mb-2">Hole {expandedHole} Details</div>
                      <div className="grid grid-cols-4 gap-1.5 text-[9px]">
                        <div className="bg-background rounded p-1.5 text-center"><div className="font-bold">{h.putts}</div><div className="text-muted-foreground">Putts</div></div>
                        <div className="bg-background rounded p-1.5 text-center"><div className="font-bold capitalize">{h.par >= 4 ? (h.drive_accuracy || '-') : 'N/A'}</div><div className="text-muted-foreground">Tee Shot</div></div>
                        <div className="bg-background rounded p-1.5 text-center"><div className="font-bold">{h.tee_club || '-'}</div><div className="text-muted-foreground">Club</div></div>
                        <div className="bg-background rounded p-1.5 text-center"><div className="font-bold">{h.gir ? '✓' : '✗'}</div><div className="text-muted-foreground">GIR</div></div>
                      </div>
                      <div className="grid grid-cols-2 gap-1.5 mt-1.5 text-[9px]">
                        <div className="bg-background rounded p-1.5">
                          <div className="font-bold text-foreground">Penalties ({h.penalties || 0})</div>
                          {(h.penalties || 0) > 0 ? (
                            <div className="flex gap-1.5 text-muted-foreground mt-0.5">
                              {(pd.h2o || 0) > 0 && <span>H2O: {pd.h2o}</span>}
                              {(pd.ob || 0) > 0 && <span>OB: {pd.ob}</span>}
                              {(pd.dropped || 0) > 0 && <span>Drop: {pd.dropped}</span>}
                            </div>
                          ) : <div className="text-muted-foreground mt-0.5">None</div>}
                        </div>
                        <div className="bg-background rounded p-1.5">
                          <div className="font-bold text-foreground">Bunkers ({h.bunkers || 0})</div>
                          {(h.bunkers || 0) > 0 ? (
                            <div className="flex gap-1.5 text-muted-foreground mt-0.5">
                              {(bd.greenside || 0) > 0 && <span>GS: {bd.greenside}</span>}
                              {(bd.fairway || 0) > 0 && <span>FW: {bd.fairway}</span>}
                            </div>
                          ) : <div className="text-muted-foreground mt-0.5">None</div>}
                        </div>
                      </div>
                      {h.first_putt_distance && (
                        <div className="mt-1.5 text-[9px] text-muted-foreground">1st Putt: <span className="font-semibold text-foreground">{h.first_putt_distance}</span></div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
const FeedView = ({ user, onSwitchTo }) => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [newPostContent, setNewPostContent] = useState('');
  const [postMediaFiles, setPostMediaFiles] = useState([]);
  const [postMediaUrls, setPostMediaUrls] = useState([]);
  const [creating, setCreating] = useState(false);
  const [commentingOn, setCommentingOn] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [postComments, setPostComments] = useState({});
  const [unreadCount, setUnreadCount] = useState(0);
  const [followingSet, setFollowingSet] = useState(new Set());
  const fileInputRef = useRef(null);

  const fetchFeed = useCallback(async () => {
    try {
      setLoading(true);
      const [feedRes, unreadRes, followRes] = await Promise.all([
        axios.get(`${API}/social/feed`),
        axios.get(`${API}/chat/unread-count`),
        axios.get(`${API}/social/following`)
      ]);
      setPosts(feedRes.data.posts);
      setUnreadCount(unreadRes.data.unread_count);
      setFollowingSet(new Set(followRes.data.following));
    } catch (error) {
      toast.error('Failed to load feed');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleFollow = async (userId) => {
    try {
      const res = await axios.post(`${API}/social/follow/${userId}`);
      setFollowingSet(prev => {
        const next = new Set(prev);
        if (res.data.following) next.add(userId);
        else next.delete(userId);
        return next;
      });
    } catch {
      toast.error('Failed to update follow');
    }
  };

  useEffect(() => {
    fetchFeed();
  }, [fetchFeed]);

  const handleCreatePost = async () => {
    if (!newPostContent.trim() && postMediaUrls.length === 0) return;
    setCreating(true);
    try {
      // Upload media files first
      const uploadedUrls = [...postMediaUrls];
      for (const file of postMediaFiles) {
        const formData = new FormData();
        formData.append('file', file);
        const res = await axios.post(`${API}/social/posts/upload-media`, formData);
        uploadedUrls.push(res.data.media_url);
      }

      const postType = uploadedUrls.length > 0 ? 'photo' : 'text';
      await axios.post(`${API}/social/posts`, {
        content: newPostContent.trim(),
        post_type: postType,
        media_urls: uploadedUrls
      });

      setNewPostContent('');
      setPostMediaFiles([]);
      setPostMediaUrls([]);
      setShowCreatePost(false);
      toast.success('Posted!');
      fetchFeed();
    } catch (error) {
      toast.error('Failed to create post');
    } finally {
      setCreating(false);
    }
  };

  const handleLike = async (postId) => {
    try {
      const res = await axios.post(`${API}/social/posts/${postId}/like`);
      setPosts(prev => prev.map(p => p.id === postId ? {
        ...p,
        liked_by_me: res.data.liked,
        like_count: res.data.like_count
      } : p));
    } catch {
      toast.error('Failed to like post');
    }
  };

  const handleDeletePost = async (postId) => {
    try {
      await axios.delete(`${API}/social/posts/${postId}`);
      setPosts(prev => prev.filter(p => p.id !== postId));
      toast.success('Post deleted');
    } catch {
      toast.error('Failed to delete post');
    }
  };

  const loadComments = async (postId) => {
    try {
      const res = await axios.get(`${API}/social/posts/${postId}/comments`);
      setPostComments(prev => ({ ...prev, [postId]: res.data.comments }));
    } catch {
      toast.error('Failed to load comments');
    }
  };

  const handleComment = async (postId) => {
    if (!commentText.trim()) return;
    try {
      const res = await axios.post(`${API}/social/posts/${postId}/comments`, {
        content: commentText.trim()
      });
      setPostComments(prev => ({
        ...prev,
        [postId]: [...(prev[postId] || []), res.data.comment]
      }));
      setPosts(prev => prev.map(p => p.id === postId ? {
        ...p, comment_count: (p.comment_count || 0) + 1
      } : p));
      setCommentText('');
    } catch {
      toast.error('Failed to add comment');
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setPostMediaFiles(prev => [...prev, ...files]);
  };

  const removeMediaFile = (idx) => {
    setPostMediaFiles(prev => prev.filter((_, i) => i !== idx));
  };

  if (loading) {
    return (
      <div className="space-y-4 pt-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="animate-pulse">
            <div className="bg-muted rounded-2xl h-64" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-0">
      {/* Top Action Bar */}
      <div className="flex items-center justify-between px-4 py-3 sticky top-0 z-20 border-b border-border bg-background/90 backdrop-blur-xl">
        <img src="/logo-the-club.png" alt="The Club" className="h-8 w-auto" data-testid="social-title" />
        <button
          onClick={() => navigate('/settings')}
          className="relative"
          data-testid="profile-avatar-btn"
        >
          {getUserTier(user) && (
            <div className="absolute -inset-[2px] rounded-full" style={{ background: TIER_STYLES[getUserTier(user)].ring }} />
          )}
          <Avatar className="w-8 h-8 border-2 border-border relative">
            <AvatarImage src={getPhotoUrl(user?.profile_photo) || getDefaultPhoto(user?.display_name)} />
            <AvatarFallback className="bg-primary text-primary-foreground text-xs">
              {user?.display_name?.charAt(0) || 'G'}
            </AvatarFallback>
          </Avatar>
        </button>
      </div>

      {/* Create Post Inline */}
      <div className="px-4 py-3 border-b border-border/30">
        <div
          className="flex items-center gap-3 cursor-pointer"
          onClick={() => setShowCreatePost(true)}
          data-testid="create-post-trigger"
        >
          <div className="relative">
            {getUserTier(user) && (
              <div className="absolute -inset-[3px] rounded-full" style={{
                background: TIER_STYLES[getUserTier(user)].ring,
              }} />
            )}
            <Avatar className="w-10 h-10 border-2 border-primary/20 relative">
              <AvatarImage src={getPhotoUrl(user?.profile_photo) || getDefaultPhoto(user?.display_name)} />
              <AvatarFallback className="bg-primary text-primary-foreground">
                {user?.display_name?.charAt(0) || 'G'}
              </AvatarFallback>
            </Avatar>
            {getUserTier(user) && (
              <div className="absolute -bottom-0.5 -right-0.5 z-10">
                <TierBadge tier={getUserTier(user)} size="sm" />
              </div>
            )}
          </div>
          <div className="flex-1 h-10 rounded-full bg-muted flex items-center px-4 text-muted-foreground text-sm">
            Share your round or thoughts...
          </div>
          <Button variant="ghost" size="icon" className="rounded-full text-primary" data-testid="create-post-btn">
            <Plus className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Feed Posts */}
      <div className="divide-y divide-border/30">
        {posts.length === 0 ? (
          <div className="text-center py-16 px-4">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
              <Users className="w-10 h-10 text-muted-foreground" />
            </div>
            <h3 className="font-serif text-lg font-semibold mb-2">No Posts Yet</h3>
            <p className="text-muted-foreground text-sm max-w-xs mx-auto">
              Match with golfers and share your rounds and experiences!
            </p>
          </div>
        ) : (
          posts.map(post => (
            <div key={post.id} className="px-4 py-4" data-testid={`post-${post.id}`}>
              {/* Post Header */}
              <div className="flex items-center gap-3 mb-3">
                <div className="relative">
                  {getUserTier(post.user) && (
                    <div className="absolute -inset-[3px] rounded-full" style={{
                      background: TIER_STYLES[getUserTier(post.user)].ring,
                    }} />
                  )}
                  <Avatar className="w-10 h-10 border border-border relative">
                    <AvatarImage src={getPhotoUrl(post.user?.profile_photo) || getDefaultPhoto(post.user?.display_name)} />
                    <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                      {post.user?.display_name?.charAt(0) || 'G'}
                    </AvatarFallback>
                  </Avatar>
                  {getUserTier(post.user) && (
                    <div className="absolute -bottom-0.5 -right-0.5 z-10">
                      <TierBadge tier={getUserTier(post.user)} size="sm" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <p className="font-semibold text-sm text-foreground truncate">
                      {post.user?.display_name || post.user?.username}
                    </p>
                    {post.user?.is_founder && <TierPill tier="founder" size="xs" />}
                    <StrokesToParBadge display_metric={post.user?.display_metric} size="sm" showLabel={false} showInfo={false} />
                    {post.is_followed_user && (
                      <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-primary/10 text-primary whitespace-nowrap" data-testid={`following-badge-${post.id}`}>
                        Following
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">{formatTime(post.created_at)}</p>
                </div>
                {post.user_id === user?.id ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="rounded-full w-8 h-8"
                    onClick={() => handleDeletePost(post.id)}
                    data-testid={`delete-post-${post.id}`}
                  >
                    <Trash2 className="w-4 h-4 text-muted-foreground" />
                  </Button>
                ) : (
                  <div className="flex items-center gap-1">
                    <Button
                      variant={followingSet.has(post.user_id) ? "secondary" : "outline"}
                      size="sm"
                      className="rounded-full gap-1 h-8 text-xs px-3"
                      onClick={() => handleFollow(post.user_id)}
                      data-testid={`follow-btn-${post.user_id}`}
                    >
                      {followingSet.has(post.user_id) ? (
                        <><UserCheck className="w-3.5 h-3.5" /> Following</>
                      ) : (
                        <><UserPlus className="w-3.5 h-3.5" /> Follow</>
                      )}
                    </Button>
                    <ReportBlockMenu
                      targetType="post"
                      targetId={post.id}
                      targetUserId={post.user_id}
                      targetLabel={post.user?.display_name || post.user?.username}
                      onBlocked={() => setPosts(prev => prev.filter(p => p.user_id !== post.user_id))}
                    />
                  </div>
                )}
              </div>

              {/* Post Content */}
              {post.content && (
                <p className="text-sm text-foreground mb-3 whitespace-pre-wrap leading-relaxed">{post.content}</p>
              )}

              {/* Grint-Style Round Stats Card */}
              {post.post_type === 'round' && post.round_data && (
                <RoundPostCard roundData={post.round_data} />
              )}

              {/* Workout Card with View/Start buttons */}
              {post.post_type === 'workout' && (
                <div className="mb-3 rounded-xl bg-gradient-to-br from-orange-500/10 to-yellow-500/5 border border-orange-500/20 overflow-hidden">
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Dumbbell className="w-3.5 h-3.5 text-orange-500" />
                      <span className="text-[10px] font-bold text-orange-500 uppercase tracking-widest">Workout Complete</span>
                    </div>
                  </div>
                  {post.workout_id && (
                    <div className="flex border-t border-orange-500/10">
                      <button
                        onClick={() => navigate(`/fitness?view=workout&id=${post.workout_id}`)}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-semibold text-orange-500 hover:bg-orange-500/5 transition-colors border-r border-orange-500/10"
                        data-testid={`view-workout-${post.id}`}
                      >
                        <ChevronUp className="w-3.5 h-3.5" />
                        View Workout
                      </button>
                      <button
                        onClick={() => navigate(`/fitness?action=start&workout_id=${post.workout_id}`)}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-semibold text-orange-500 hover:bg-orange-500/5 transition-colors"
                        data-testid={`start-workout-${post.id}`}
                      >
                        <Play className="w-3.5 h-3.5" />
                        Start Workout
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Post Media */}
              {post.media_urls && post.media_urls.length > 0 && (
                <div className={`mb-3 rounded-xl overflow-hidden ${post.media_urls.length > 1 ? 'grid grid-cols-2 gap-0.5' : ''}`}>
                  {post.media_urls.map((url) => (
                    <img
                      key={url}
                      src={getPhotoUrl(url)}
                      alt="Post media"
                      className="w-full object-cover max-h-96"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ))}
                </div>
              )}

              {/* Post Actions */}
              <div className="flex items-center gap-6 mt-2">
                <button
                  onClick={() => handleLike(post.id)}
                  className={`flex items-center gap-1.5 text-sm transition-colors ${post.liked_by_me ? 'text-destructive' : 'text-muted-foreground hover:text-destructive'}`}
                  data-testid={`like-btn-${post.id}`}
                >
                  <Heart className={`w-5 h-5 ${post.liked_by_me ? 'fill-current' : ''}`} />
                  <span>{post.like_count || 0}</span>
                </button>
                <button
                  onClick={() => {
                    if (commentingOn === post.id) {
                      setCommentingOn(null);
                    } else {
                      setCommentingOn(post.id);
                      if (!postComments[post.id]) loadComments(post.id);
                    }
                  }}
                  className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors"
                  data-testid={`comment-btn-${post.id}`}
                >
                  <MessageCircle className="w-5 h-5" />
                  <span>{post.comment_count || 0}</span>
                </button>
              </div>

              {/* Comments Section */}
              {commentingOn === post.id && (
                <div className="mt-3 pl-2 border-l-2 border-border/50 space-y-3">
                  {(postComments[post.id] || []).map(c => (
                    <div key={c.id} className="flex gap-2" data-testid={`comment-${c.id}`}>
                      <Avatar className="w-7 h-7 mt-0.5">
                        <AvatarImage src={getPhotoUrl(c.user?.profile_photo) || getDefaultPhoto(c.user?.display_name)} />
                        <AvatarFallback className="text-xs bg-muted">{c.user?.display_name?.charAt(0) || '?'}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <p className="text-xs">
                          <span className="font-semibold">{c.user?.display_name}</span>{' '}
                          <span className="text-muted-foreground">{c.content}</span>
                        </p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">{formatTime(c.created_at)}</p>
                      </div>
                    </div>
                  ))}
                  <div className="flex gap-2 items-center">
                    <Input
                      value={commentText}
                      onChange={e => setCommentText(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') handleComment(post.id); }}
                      placeholder="Add a comment..."
                      className="h-8 text-sm rounded-full bg-muted border-0 px-3"
                      data-testid={`comment-input-${post.id}`}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="w-8 h-8 rounded-full"
                      onClick={() => handleComment(post.id)}
                      disabled={!commentText.trim()}
                      data-testid={`comment-send-${post.id}`}
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Create Post Dialog */}
      <Dialog open={showCreatePost} onOpenChange={setShowCreatePost}>
        <DialogContent className="sm:max-w-lg rounded-3xl border-border/50">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Create Post</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-3">
              <Avatar className="w-10 h-10 border border-border">
                <AvatarImage src={getPhotoUrl(user?.profile_photo) || getDefaultPhoto(user?.display_name)} />
                <AvatarFallback className="bg-primary text-primary-foreground">
                  {user?.display_name?.charAt(0) || 'G'}
                </AvatarFallback>
              </Avatar>
              <textarea
                value={newPostContent}
                onChange={e => setNewPostContent(e.target.value)}
                placeholder="What's on the fairway today?"
                className="flex-1 min-h-[100px] bg-transparent text-sm resize-none outline-none text-foreground placeholder:text-muted-foreground"
                autoFocus
                data-testid="post-content-input"
              />
            </div>

            {/* Media Previews */}
            {postMediaFiles.length > 0 && (
              <div className="flex gap-2 flex-wrap">
                {postMediaFiles.map((file, idx) => (
                  <div key={`${file.name}-${file.size}-${file.lastModified}`} className="relative w-20 h-20 rounded-lg overflow-hidden bg-muted">
                    <img
                      src={URL.createObjectURL(file)}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                    <button
                      onClick={() => removeMediaFile(idx)}
                      className="absolute top-0.5 right-0.5 w-5 h-5 bg-black/60 rounded-full flex items-center justify-center"
                    >
                      <X className="w-3 h-3 text-white" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center justify-between pt-2 border-t border-border/50">
              <div className="flex gap-2">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept="image/*,video/*"
                  multiple
                  className="hidden"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  className="rounded-full gap-1.5 text-primary"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="add-media-btn"
                >
                  <ImageIcon className="w-4 h-4" />
                  Photo/Video
                </Button>
              </div>
              <Button
                onClick={handleCreatePost}
                disabled={creating || (!newPostContent.trim() && postMediaFiles.length === 0)}
                className="rounded-full px-6"
                data-testid="submit-post-btn"
              >
                {creating ? 'Posting...' : 'Post'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============== Discover Sub-View ==============
const DiscoverView = ({ user, onBack }) => {
  const [golfers, setGolfers] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [swiping, setSwiping] = useState(null);
  const [matchModal, setMatchModal] = useState(null);
  const [followingSet, setFollowingSet] = useState(new Set());
  const navigate = useNavigate();

  const fetchGolfers = useCallback(async () => {
    try {
      setLoading(true);
      const [golfersRes, followRes] = await Promise.all([
        axios.get(`${API}/discover`),
        axios.get(`${API}/social/following`)
      ]);
      setGolfers(golfersRes.data);
      setCurrentIndex(0);
      setFollowingSet(new Set(followRes.data.following));
    } catch {
      toast.error('Failed to load golfers');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleFollowDiscover = async (userId) => {
    try {
      const res = await axios.post(`${API}/social/follow/${userId}`);
      setFollowingSet(prev => {
        const next = new Set(prev);
        if (res.data.following) next.add(userId);
        else next.delete(userId);
        return next;
      });
    } catch {
      toast.error('Failed to update follow');
    }
  };

  useEffect(() => { fetchGolfers(); }, [fetchGolfers]);

  const handleSwipe = async (action) => {
    if (currentIndex >= golfers.length) return;
    const currentGolfer = golfers[currentIndex];
    setSwiping(action);
    try {
      const response = await axios.post(`${API}/swipe`, {
        target_user_id: currentGolfer.id, action
      });
      if (response.data.is_match) {
        setMatchModal(response.data.match);
      }
      setTimeout(() => {
        setSwiping(null);
        setCurrentIndex(prev => prev + 1);
      }, 300);
    } catch {
      toast.error('Something went wrong');
      setSwiping(null);
    }
  };

  const currentGolfer = golfers[currentIndex];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 sticky top-0 z-20 bg-background/80 backdrop-blur-xl border-b border-border/50">
        <Button variant="ghost" size="icon" onClick={onBack} className="rounded-full" data-testid="discover-back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="font-serif text-xl font-bold">Discover</h1>
        <div className="flex-1" />
        <Button variant="ghost" size="icon" onClick={fetchGolfers} className="rounded-full" data-testid="discover-refresh-btn">
          <RefreshCw className="w-5 h-5" />
        </Button>
      </div>

      <div className="max-w-lg mx-auto px-4 pt-4">
        {loading ? (
          <div className="animate-pulse"><div className="h-[480px] bg-muted rounded-3xl" /></div>
        ) : !currentGolfer ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
              <Users className="w-10 h-10 text-muted-foreground" />
            </div>
            <h2 className="font-serif text-lg font-semibold mb-2">No More Golfers</h2>
            <p className="text-muted-foreground text-sm mb-4">Check back later for new players!</p>
            <Button onClick={fetchGolfers} className="rounded-full gap-2" data-testid="discover-reload-btn">
              <RefreshCw className="w-4 h-4" /> Refresh
            </Button>
          </div>
        ) : (
          <>
            <Card
              className={`relative overflow-hidden rounded-3xl shadow-xl h-[480px] transition-all duration-300 ${
                swiping === 'like' ? 'animate-slide-right opacity-0' :
                swiping === 'pass' ? 'animate-slide-left opacity-0' : ''
              }`}
              data-testid="golfer-card"
            >
              <img
                src={getPhotoUrl(currentGolfer.profile_photo) || getDefaultPhoto(currentGolfer.display_name)}
                alt={currentGolfer.display_name}
                className="absolute inset-0 w-full h-full object-cover"
                onError={(e) => { e.target.src = getDefaultPhoto(currentGolfer.display_name); }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <h2 className="font-serif text-2xl font-bold mb-1" data-testid="golfer-name">
                  {currentGolfer.display_name}, {currentGolfer.age}
                </h2>
                {currentGolfer.location && (
                  <div className="flex items-center gap-1 text-white/80 text-sm mb-3">
                    <MapPin className="w-4 h-4" /><span>{currentGolfer.location}</span>
                  </div>
                )}
                <div className="flex flex-wrap gap-2 mb-2">
                  {currentGolfer.handicap && (
                    <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-white/20 backdrop-blur-sm text-xs">
                      <Trophy className="w-3 h-3" /> Handicap: {currentGolfer.handicap}
                    </span>
                  )}
                  {currentGolfer.is_private_club_member && (
                    <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-secondary/80 text-secondary-foreground text-xs">
                      <Sparkles className="w-3 h-3" /> Club Member
                    </span>
                  )}
                </div>
                {currentGolfer.bio && (
                  <p className="text-white/90 text-sm line-clamp-2">{currentGolfer.bio}</p>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-2 rounded-full gap-1.5 text-white/90 hover:text-white hover:bg-white/20 border border-white/30 h-8 text-xs px-3"
                  onClick={(e) => { e.stopPropagation(); handleFollowDiscover(currentGolfer.id); }}
                  data-testid="discover-follow-btn"
                >
                  {followingSet.has(currentGolfer.id) ? (
                    <><UserCheck className="w-3.5 h-3.5" /> Following</>
                  ) : (
                    <><UserPlus className="w-3.5 h-3.5" /> Follow</>
                  )}
                </Button>
              </div>
            </Card>

            <div className="flex items-center justify-center gap-8 mt-5">
              <Button
                variant="outline"
                size="lg"
                onClick={() => handleSwipe('pass')}
                disabled={swiping}
                className="w-14 h-14 rounded-full border-2 border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground btn-press"
                data-testid="pass-btn"
              >
                <ThumbsDown className="w-7 h-7" />
              </Button>
              <Button
                size="lg"
                onClick={() => handleSwipe('like')}
                disabled={swiping}
                className="w-18 h-18 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg btn-press animate-pulse-glow p-5"
                data-testid="like-btn"
              >
                <ThumbsUp className="w-8 h-8" />
              </Button>
            </div>
            <p className="text-center text-muted-foreground text-xs mt-3">
              {golfers.length - currentIndex - 1} more golfers
            </p>
          </>
        )}
      </div>

      {/* Match Modal */}
      <Dialog open={!!matchModal} onOpenChange={() => setMatchModal(null)}>
        <DialogContent className="sm:max-w-md rounded-3xl border-border/50">
          <DialogHeader>
            <DialogTitle className="text-center font-serif text-2xl">It's a Match!</DialogTitle>
          </DialogHeader>
          <div className="text-center py-6">
            <div className="flex justify-center items-center gap-4 mb-6">
              <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-primary">
                <img
                  src={getPhotoUrl(user?.profile_photo) || getDefaultPhoto(user?.display_name)}
                  alt="You" className="w-full h-full object-cover"
                />
              </div>
              <Heart className="w-8 h-8 text-primary fill-primary animate-pulse" />
              <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-secondary">
                <img
                  src={getPhotoUrl(matchModal?.matched_user?.profile_photo) || getDefaultPhoto(matchModal?.matched_user?.display_name)}
                  alt={matchModal?.matched_user?.display_name} className="w-full h-full object-cover"
                />
              </div>
            </div>
            <p className="text-muted-foreground mb-6">
              You and <span className="font-semibold text-foreground">{matchModal?.matched_user?.display_name}</span> both liked each other!
            </p>
            <div className="flex gap-4">
              <Button variant="outline" onClick={() => setMatchModal(null)} className="flex-1 rounded-full">
                Keep Exploring
              </Button>
              <Button
                onClick={() => { setMatchModal(null); navigate(`/messages/${matchModal?.match_id}`); }}
                className="flex-1 rounded-full bg-secondary hover:bg-secondary/90 text-secondary-foreground"
                data-testid="send-message-btn"
              >
                Send Message
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============== Messages List Sub-View ==============
const MessagesView = ({ onBack }) => {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchMatches = async () => {
      try {
        const response = await axios.get(`${API}/matches`);
        setMatches(response.data);
      } catch {
        toast.error('Failed to load conversations');
      } finally {
        setLoading(false);
      }
    };
    fetchMatches();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 sticky top-0 z-20 bg-background/80 backdrop-blur-xl border-b border-border/50">
        <Button variant="ghost" size="icon" onClick={onBack} className="rounded-full" data-testid="messages-back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="font-serif text-xl font-bold">Messages</h1>
          <p className="text-xs text-muted-foreground">{matches.length} conversations</p>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 pt-2">
        {loading ? (
          <div className="space-y-3 pt-2">
            {[1, 2, 3].map(i => (
              <div key={i} className="animate-pulse flex items-center gap-4 p-4 bg-muted rounded-2xl">
                <div className="w-14 h-14 bg-muted-foreground/20 rounded-full" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-muted-foreground/20 rounded w-1/2" />
                  <div className="h-3 bg-muted-foreground/20 rounded w-3/4" />
                </div>
              </div>
            ))}
          </div>
        ) : matches.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
              <MessageCircle className="w-10 h-10 text-muted-foreground" />
            </div>
            <h2 className="font-serif text-lg font-semibold mb-2">No Messages Yet</h2>
            <p className="text-muted-foreground text-sm">Match with golfers to start chatting!</p>
          </div>
        ) : (
          <div className="space-y-2 pt-2">
            {matches.map(match => (
              <Card
                key={match.id}
                className="p-3 rounded-2xl border-border/50 hover:border-primary/30 transition-all cursor-pointer card-hover"
                onClick={() => navigate(`/messages/${match.id}`)}
                data-testid={`match-card-${match.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <Avatar className="w-14 h-14 border-2 border-primary/20">
                      <AvatarImage
                        src={getPhotoUrl(match.other_user?.profile_photo) || getDefaultPhoto(match.other_user?.display_name)}
                      />
                      <AvatarFallback className="bg-primary text-primary-foreground font-semibold">
                        {match.other_user?.display_name?.charAt(0) || 'G'}
                      </AvatarFallback>
                    </Avatar>
                    {match.unread_count > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center px-1">
                        {match.unread_count}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-0.5">
                      <h3 className="font-semibold text-sm text-foreground truncate">
                        {match.other_user?.display_name}
                      </h3>
                      {(match.last_message || match.created_at) && (
                        <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                          <Clock className="w-3 h-3" />
                          {formatTime(match.last_message?.created_at || match.created_at)}
                        </span>
                      )}
                    </div>
                    {match.last_message ? (
                      <p className="text-xs text-muted-foreground truncate">{match.last_message.content}</p>
                    ) : (
                      <p className="text-xs text-primary">Start a conversation</p>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ============== Main Social Page ==============
const Social = () => {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-background pb-24">
      <Navigation />
      <TooltipGuide pageKey="social" />
      <FeedView user={user} onSwitchTo={() => {}} />
    </div>
  );
};

export default Social;
